import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import {
  MessageSquare,
  ChevronLeft,
  Send,
  Search,
  Users,
  Shield,
  Lock,
  UserCheck,
  Building2,
  Plus,
  CheckCheck,
  Clock,
  Smile,
  MoreVertical,
  Phone,
  Video,
  Info,
  ArrowLeft,
  Sparkles,
  Crown,
  Bell
} from 'lucide-react';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';

const MessagesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [pendingContact, setPendingContact] = useState(null);
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'
  const [isTyping, setIsTyping] = useState(false); // Is the other person typing
  const [typingUser, setTypingUser] = useState(null);
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const isClubMember = hasFullAccess(user?.subscription_tier);

  const targetUserId = searchParams.get('user');
  const targetUserName = searchParams.get('name');
  const propertyTitle = searchParams.get('property');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isClubMember) {
      fetchConversations();
      fetchMembers();
    }
  }, [isClubMember]);

  useEffect(() => {
    if (!targetUserId) return;
    
    const existingConv = conversations.find(c => c.other_user?.user_id === targetUserId);
    
    if (existingConv) {
      setSelectedConversation(existingConv);
      fetchMessages(existingConv.conversation_id);
      setMobileView('chat');
      if (propertyTitle && !newMessage) {
        setNewMessage(`Bonjour ! Je suis intéressé(e) par votre bien "${propertyTitle}". Pourriez-vous me donner plus d'informations ?`);
      }
      setPendingContact(null);
    } else if (targetUserName || members.length > 0) {
      const member = members.find(m => m.user_id === targetUserId);
      setPendingContact({
        user_id: targetUserId,
        name: targetUserName || member?.name || 'Membre',
        email: member?.email,
        propertyTitle
      });
      setShowMembers(false);
      setSelectedConversation(null);
      setMobileView('chat');
    }
  }, [targetUserId, targetUserName, propertyTitle, conversations, members]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Polling for new messages and typing status
  useEffect(() => {
    if (!isClubMember || !selectedConversation?.conversation_id) return;

    // Poll for new messages every 3 seconds
    const messageInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/messages/conversation/${selectedConversation.conversation_id}`, { withCredentials: true });
        if (response.data.length > messages.length) {
          setMessages(response.data);
          // Show notification for new message
          const newMsg = response.data[response.data.length - 1];
          if (newMsg.sender_id !== user?.user_id) {
            toast.success(`Nouveau message de ${newMsg.sender_name || 'un membre'}`, {
              icon: <Bell className="w-4 h-4" />,
              duration: 3000
            });
          }
        }
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    }, 3000);

    // Poll for typing status every 1.5 seconds
    const typingInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/messages/typing/${selectedConversation.conversation_id}`, { withCredentials: true });
        if (response.data.typing && response.data.typing.length > 0) {
          setIsTyping(true);
          setTypingUser(response.data.typing[0]);
        } else {
          setIsTyping(false);
          setTypingUser(null);
        }
      } catch (error) {
        console.error('Error polling typing:', error);
      }
    }, 1500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(typingInterval);
    };
  }, [isClubMember, selectedConversation?.conversation_id, messages.length, user?.user_id]);

  // Send typing indicator when user types
  const handleTyping = useCallback(async () => {
    if (!selectedConversation?.conversation_id) return;
    
    const now = Date.now();
    // Only send typing indicator every 2 seconds
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;

    try {
      await axios.post(`${API}/messages/typing/${selectedConversation.conversation_id}`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Error sending typing status:', error);
    }

    // Clear typing status after 3 seconds of no typing
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.delete(`${API}/messages/typing/${selectedConversation.conversation_id}`, { withCredentials: true });
      } catch (error) {
        console.error('Error clearing typing status:', error);
      }
    }, 3000);
  }, [selectedConversation?.conversation_id]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${conversationId}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/community/members`, { withCredentials: true });
      setMembers(response.data.filter(m => m.user_id !== user?.user_id));
    } catch (error) {
      console.error(error);
    }
  };

  const startConversation = async (member, initialMessage = null) => {
    try {
      const existing = conversations.find(c => c.other_user?.user_id === member.user_id);
      
      if (existing) {
        setSelectedConversation(existing);
        fetchMessages(existing.conversation_id);
        setShowMembers(false);
        setPendingContact(null);
        setMobileView('chat');
        return;
      }

      const messageContent = initialMessage || 'Bonjour !';
      await axios.post(`${API}/messages`, {
        recipient_id: member.user_id,
        content: messageContent
      }, { withCredentials: true });
      
      await fetchConversations();
      setShowMembers(false);
      setPendingContact(null);
      setNewMessage('');
      toast.success('Conversation démarrée');
      
      const newConversations = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      const newConv = newConversations.data.find(c => c.other_user?.user_id === member.user_id);
      if (newConv) {
        setSelectedConversation(newConv);
        fetchMessages(newConv.conversation_id);
        setMobileView('chat');
      }
    } catch (error) {
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={MessageSquare}
        iconBgClass="from-violet-500 to-purple-600"
        iconColorClass="text-violet-500"
        buttonGradientClass="from-violet-500 to-purple-600"
        title="Messagerie Privée"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Users, title: "Réseau d'investisseurs", description: "Échangez avec des investisseurs et professionnels de l'immobilier" },
          { icon: Shield, title: "Conversations sécurisées", description: "Vos échanges restent confidentiels entre membres du Club" },
          { icon: UserCheck, title: "Membres vérifiés", description: "Connectez-vous avec des investisseurs sérieux et engagés" }
        ]}
      />
    );
  }

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.conversation_id);
    setShowMembers(false);
    setMobileView('chat');
  };

  const startNewConversation = async (memberId) => {
    const existingConv = conversations.find(c => c.other_user_id === memberId);
    if (existingConv) {
      selectConversation(existingConv);
      return;
    }

    const member = members.find(m => m.user_id === memberId);
    setSelectedConversation({
      other_user_id: memberId,
      other_user_name: member?.name,
      conversation_id: `conv_${[user.user_id, memberId].sort().join('_')}`
    });
    setMessages([]);
    setShowMembers(false);
    setMobileView('chat');
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedConversation.other_user_id,
        content: newMessage
      }, { withCredentials: true });

      setMessages([...messages, {
        message_id: `temp_${Date.now()}`,
        sender_id: user.user_id,
        content: newMessage,
        created_at: new Date().toISOString()
      }]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const formatMessageDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      if (isToday(date)) {
        return format(date, 'HH:mm', { locale: fr });
      } else if (isYesterday(date)) {
        return 'Hier ' + format(date, 'HH:mm', { locale: fr });
      }
      return format(date, 'dd/MM HH:mm', { locale: fr });
    } catch {
      return '';
    }
  };

  const formatConversationDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      if (isToday(date)) {
        return format(date, 'HH:mm', { locale: fr });
      } else if (isYesterday(date)) {
        return 'Hier';
      }
      return format(date, 'dd/MM', { locale: fr });
    } catch {
      return '';
    }
  };

  // Generate consistent color for avatar based on name
  const getAvatarGradient = (name) => {
    const gradients = [
      'from-violet-500 to-purple-600',
      'from-blue-500 to-indigo-600',
      'from-emerald-500 to-teal-600',
      'from-amber-500 to-orange-600',
      'from-rose-500 to-pink-600',
      'from-cyan-500 to-blue-600',
    ];
    const index = name ? name.charCodeAt(0) % gradients.length : 0;
    return gradients[index];
  };

  const totalUnread = conversations.reduce((acc, conv) => acc + (conv.unread || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className={mobileView === 'chat' ? 'hidden lg:flex' : 'flex'}>
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1]">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Tableau de bord
                </Button>
              </Link>
              {mobileView === 'chat' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden text-slate-600"
                  onClick={() => setMobileView('list')}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Retour
                </Button>
              )}
              <Link to="/directory" data-testid="view-members-link">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1]">
                  <Users className="h-4 w-4 mr-1" />
                  Voir les membres
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-3">
              {totalUnread > 0 && (
                <Badge className="bg-violet-500 text-white">
                  {totalUnread} non lu{totalUnread > 1 ? 's' : ''}
                </Badge>
              )}
              <Button
                onClick={() => { setShowMembers(true); setMobileView('list'); }}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-500/25"
                data-testid="new-message-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Nouveau message</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">Messagerie Privée</h1>
                <p className="text-violet-200 text-xs sm:text-base">Échangez en toute confidentialité</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{conversations.length}</div>
                <div className="text-violet-200 text-sm">Conversations</div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{members.length}</div>
                <div className="text-violet-200 text-sm">Membres</div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl">
                <Shield className="w-5 h-5 text-emerald-300" />
                <span className="text-white text-sm font-medium">Chiffré</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-3 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-6" style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}>
          
          {/* Conversations List */}
          <div className={`lg:col-span-4 ${mobileView === 'chat' ? 'hidden lg:block' : 'block'}`}>
            <Card className="h-full border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-slate-900 text-lg">
                    {showMembers ? 'Membres' : 'Messages'}
                  </h2>
                  {!showMembers && conversations.length > 0 && (
                    <Badge variant="outline" className="text-slate-500">
                      {conversations.length}
                    </Badge>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={showMembers ? "Rechercher un membre..." : "Rechercher..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-50 border-0 focus:bg-white focus:ring-2 focus:ring-violet-500"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <ScrollArea className="h-[calc(100%-100px)]">
                {showMembers ? (
                  <div className="p-3 space-y-1">
                    {filteredMembers.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">Aucun membre trouvé</p>
                      </div>
                    ) : (
                      filteredMembers.map((member) => (
                        <button
                          key={member.user_id}
                          onClick={() => startNewConversation(member.user_id)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-violet-50 transition-all group"
                          data-testid={`member-${member.user_id}`}
                        >
                          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarGradient(member.name)} flex items-center justify-center text-white font-semibold shadow-md group-hover:scale-105 transition-transform`}>
                            {member.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="font-semibold text-slate-900">{member.name}</div>
                            <div className="text-sm text-slate-500 truncate">{member.email}</div>
                          </div>
                          <MessageSquare className="w-5 h-5 text-slate-400 group-hover:text-violet-500 transition-colors" />
                        </button>
                      ))
                    )}
                    <button
                      onClick={() => setShowMembers(false)}
                      className="w-full mt-4 text-center text-violet-600 hover:text-violet-700 text-sm font-medium py-2"
                    >
                      ← Retour aux conversations
                    </button>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-20 h-20 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="h-10 w-10 text-violet-400" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">Aucune conversation</h3>
                    <p className="text-slate-500 text-sm mb-4">Démarrez une discussion avec un membre du Club</p>
                    <Button
                      onClick={() => setShowMembers(true)}
                      className="bg-gradient-to-r from-violet-500 to-purple-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau message
                    </Button>
                  </div>
                ) : (
                  <div className="p-2">
                    {conversations.map((conv) => {
                      const isSelected = selectedConversation?.conversation_id === conv.conversation_id;
                      return (
                        <button
                          key={conv.conversation_id}
                          onClick={() => selectConversation(conv)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all mb-1 ${
                            isSelected 
                              ? 'bg-violet-100 border-2 border-violet-300' 
                              : 'hover:bg-slate-50'
                          }`}
                          data-testid={`conversation-${conv.conversation_id}`}
                        >
                          <div className="relative">
                            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarGradient(conv.other_user_name)} flex items-center justify-center text-white font-semibold shadow-md`}>
                              {conv.other_user_name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            {/* Online indicator */}
                            <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white" />
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className={`font-semibold truncate ${isSelected ? 'text-violet-900' : 'text-slate-900'}`}>
                                {conv.other_user_name}
                              </span>
                              <span className="text-xs text-slate-400 ml-2 flex-shrink-0">
                                {formatConversationDate(conv.last_message_at)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-slate-500 truncate pr-2">{conv.last_message}</p>
                              {conv.unread > 0 && (
                                <span className="flex-shrink-0 w-5 h-5 bg-violet-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                                  {conv.unread}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </Card>
          </div>

          {/* Chat Area */}
          <div className={`lg:col-span-8 ${mobileView === 'list' ? 'hidden lg:block' : 'block'}`}>
            <Card className="h-full border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-slate-100 bg-white/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${getAvatarGradient(selectedConversation.other_user_name)} flex items-center justify-center text-white font-semibold shadow-md`}>
                            {selectedConversation.other_user_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{selectedConversation.other_user_name}</div>
                          {isTyping ? (
                            <div className="text-xs text-violet-600 flex items-center gap-1 animate-pulse">
                              <span className="flex gap-0.5">
                                <span className="w-1 h-1 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-1 h-1 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="w-1 h-1 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </span>
                              <span className="ml-1">est en train d'écrire...</span>
                            </div>
                          ) : (
                            <div className="text-xs text-emerald-600 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                              En ligne
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-violet-100 text-violet-700 border-0">
                          <Crown className="w-3 h-3 mr-1" />
                          Membre Club
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Sparkles className="w-8 h-8 text-violet-400" />
                          </div>
                          <p className="text-slate-500">Démarrez la conversation !</p>
                        </div>
                      ) : (
                        messages.map((msg, index) => {
                          const isMine = msg.sender_id === user?.user_id;
                          const showAvatar = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;
                          return (
                            <div
                              key={msg.message_id}
                              className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              {!isMine && showAvatar && (
                                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(selectedConversation.other_user_name)} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}>
                                  {selectedConversation.other_user_name?.charAt(0).toUpperCase() || 'U'}
                                </div>
                              )}
                              {!isMine && !showAvatar && <div className="w-8" />}
                              <div className={`group max-w-[70%] relative`}>
                                <div
                                  className={`px-4 py-2.5 rounded-2xl ${
                                    isMine
                                      ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-br-md'
                                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                                  }`}
                                >
                                  <p className="text-sm leading-relaxed">{msg.content}</p>
                                </div>
                                <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
                                  <span className="text-xs text-slate-400">
                                    {formatMessageDate(msg.created_at)}
                                  </span>
                                  {isMine && (
                                    <CheckCheck className="w-3.5 h-3.5 text-violet-400" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Typing Indicator */}
                  {isTyping && typingUser && (
                    <div className="px-4 py-2 bg-slate-50/80">
                      <div className="flex items-center gap-2 text-sm text-violet-600">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${getAvatarGradient(typingUser.name)} flex items-center justify-center text-white text-xs font-semibold`}>
                          {typingUser.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <span className="flex items-center gap-1">
                          <span className="font-medium">{typingUser.name}</span>
                          <span>est en train d'écrire</span>
                          <span className="flex gap-0.5 ml-1">
                            <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Message Input */}
                  <div className="p-4 border-t border-slate-100 bg-white/50">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 relative">
                        <Input
                          placeholder="Écrivez votre message..."
                          value={newMessage}
                          onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          className="pr-12 h-12 bg-slate-50 border-0 focus:bg-white focus:ring-2 focus:ring-violet-500 rounded-xl"
                          data-testid="message-input"
                        />
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          <Smile className="w-5 h-5" />
                        </button>
                      </div>
                      <Button
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                        className="h-12 w-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-500/25 disabled:opacity-50"
                        data-testid="send-message-btn"
                      >
                        <Send className="h-5 w-5" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-3 text-xs text-slate-400">
                      <Lock className="w-3 h-3" />
                      Messages chiffrés de bout en bout
                    </div>
                  </div>
                </>
              ) : pendingContact ? (
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col items-center justify-center p-6">
                    <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${getAvatarGradient(pendingContact.name)} flex items-center justify-center text-white text-3xl font-semibold shadow-xl mb-4`}>
                      {pendingContact.name?.charAt(0).toUpperCase() || 'M'}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      {pendingContact.name}
                    </h3>
                    <Badge className="bg-violet-100 text-violet-700 border-0 mb-4">
                      <Crown className="w-3 h-3 mr-1" />
                      Membre Club
                    </Badge>
                    {pendingContact.propertyTitle && (
                      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-4 py-3 rounded-xl mb-4">
                        <Building2 className="w-5 h-5 text-violet-500" />
                        <span>À propos du bien : <strong>{pendingContact.propertyTitle}</strong></span>
                      </div>
                    )}
                    <p className="text-slate-500 text-center max-w-md">
                      Envoyez un message pour démarrer la conversation
                    </p>
                  </div>
                  <div className="p-4 border-t border-slate-100 bg-white/50">
                    <div className="flex items-center gap-3">
                      <Input
                        placeholder="Votre message..."
                        value={newMessage || (pendingContact.propertyTitle ? `Bonjour ! Je suis intéressé(e) par votre bien "${pendingContact.propertyTitle}". Pourriez-vous me donner plus d'informations ?` : '')}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const msg = newMessage || `Bonjour ! Je suis intéressé(e) par votre bien "${pendingContact.propertyTitle}". Pourriez-vous me donner plus d'informations ?`;
                            startConversation(pendingContact, msg);
                          }
                        }}
                        className="flex-1 h-12 bg-slate-50 border-0 rounded-xl"
                        data-testid="new-conv-message-input"
                      />
                      <Button
                        onClick={() => {
                          const msg = newMessage || (pendingContact.propertyTitle ? `Bonjour ! Je suis intéressé(e) par votre bien "${pendingContact.propertyTitle}". Pourriez-vous me donner plus d'informations ?` : 'Bonjour !');
                          startConversation(pendingContact, msg);
                        }}
                        className="h-12 px-6 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600"
                        data-testid="start-conv-btn"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Envoyer
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Empty State */
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center max-w-md">
                    <div className="w-24 h-24 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <MessageSquare className="h-12 w-12 text-violet-500" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Sélectionnez une conversation</h3>
                    <p className="text-slate-500 mb-6">
                      Choisissez un membre dans la liste ou démarrez une nouvelle conversation
                    </p>
                    <Button
                      onClick={() => { setShowMembers(true); setMobileView('list'); }}
                      className="bg-gradient-to-r from-violet-500 to-purple-600 shadow-lg shadow-violet-500/25"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle conversation
                    </Button>
                    
                    {/* Security Info */}
                    <div className="mt-8 p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-center gap-2 text-emerald-600 mb-2">
                        <Shield className="w-5 h-5" />
                        <span className="font-semibold">Messagerie sécurisée</span>
                      </div>
                      <p className="text-sm text-slate-500">
                        Vos conversations restent privées et ne sont visibles que par vous et votre interlocuteur.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
